import type {
  Client,
  JsonValue,
  MultiProvider,
  Provider,
} from '@openfeature/web-sdk';
import { ClientProviderEvents, OpenFeature } from '@openfeature/web-sdk';
import { OverrideProvider, createMultiProvider } from 'providers';
import type {
  FlagOverride,
  FlagType,
  FlagValue,
  HookOptions,
  MultiProviderConfig,
  OnError,
} from 'types';
import type { Flags } from 'types/flags';

export class FeatureFlagClient<FF extends Flags = Flags> {
  #client: Client;
  #defaults: FF;
  #overrideProvider: OverrideProvider | null;
  #multiProvider: MultiProvider | null;

  private constructor(
    client: Client,
    defaults: FF,
    overrideProvider?: OverrideProvider,
    multiProvider?: MultiProvider,
  ) {
    this.#client = client;
    this.#defaults = defaults;
    this.#overrideProvider = overrideProvider ?? null;
    this.#multiProvider = multiProvider ?? null;
  }

  public static async create<FF extends Flags = Flags>(
    config: MultiProviderConfig,
    defaults: FF,
    onError?: OnError,
  ): Promise<FeatureFlagClient<FF>> {
    let overrideProvider: OverrideProvider | undefined;
    let multiProvider: MultiProvider | undefined;
    try {
      multiProvider = createMultiProvider(config, onError);
      // providerEntries is populated in the MultiProvider constructor, so we
      // can read it before init completes.
      overrideProvider = multiProvider.providerEntries
        .map((entry) => entry.provider)
        .find(OverrideProvider.isInstance);
      // setProviderAndWait registers the provider with OpenFeature and runs
      // its init exactly once.
      await OpenFeature.setProviderAndWait(multiProvider);
    } catch (error) {
      onError?.(
        new Error(
          `[FeatureFlagClient] Failed to initialize: ${error instanceof Error ? error.message : String(error)}`,
        ),
      );
    }
    const client = OpenFeature.getClient();
    return new this(client, defaults, overrideProvider, multiProvider);
  }

  /**
   * Whether every provider has given its answer — real values, or defaults it
   * will not improve on. False only while one may still deliver after a
   * timed-out init.
   *
   * A closed client has no providers left to ask, so it reports `true` rather
   * than stranding a consumer that still holds it.
   */
  get hasFinalValues(): boolean {
    const entries = this.#multiProvider?.providerEntries ?? [];
    return entries.every((entry) => {
      // Structural, not nominal: any provider may opt into reporting a pending
      // delivery, and this must not couple the client to a specific one.
      const { hasFinalValues } = entry.provider as Provider & {
        hasFinalValues?: boolean;
      };
      return hasFinalValues ?? true;
    });
  }

  /**
   * Fires when a provider delivers values after a timed-out init. Providers
   * signal this with OpenFeature's ConfigurationChanged, which MultiProvider
   * forwards upward (StatusTracker re-emits it verbatim). Returns an
   * unsubscribe function.
   */
  onConfigurationChanged(listener: () => void): () => void {
    const handler = () => listener();
    this.#client.addHandler(ClientProviderEvents.ConfigurationChanged, handler);
    return () => {
      this.#client.removeHandler(
        ClientProviderEvents.ConfigurationChanged,
        handler,
      );
    };
  }

  /**
   * Close the underlying providers (LaunchDarkly) and release their
   * resources. Idempotent. Call on unmount or when switching context so
   * in-flight init attempts don't linger on a discarded provider.
   */
  public async close(): Promise<void> {
    if (!this.#multiProvider) {
      return;
    }
    const provider = this.#multiProvider;
    this.#multiProvider = null;
    try {
      await provider.onClose?.();
    } catch {
      // Teardown errors aren't actionable — the caller may already be gone.
    }
  }

  isFeatureActive(feature: keyof FF & string, options: HookOptions = {}) {
    const defaultValue = Boolean(this.#defaults[feature] ?? false);
    return this.#withOverrideControl(options.disableLocalOverrides, () =>
      this.#client.getBooleanValue(feature, defaultValue, options.attributes),
    );
  }

  getFlagValue<F extends keyof FF & string>(
    feature: F,
    options: HookOptions = {},
  ): FF[F] {
    const defaultValue = this.#defaults[feature];
    return this.#withOverrideControl(options.disableLocalOverrides, () =>
      this.#getValue(feature, defaultValue, options.attributes),
    );
  }

  #getValue<F extends keyof FF & string>(
    feature: F,
    defaultValue: FF[F],
    attributes?: HookOptions['attributes'],
  ): FF[F] {
    switch (typeof defaultValue) {
      case 'boolean':
        return this.#client.getBooleanValue(
          feature,
          defaultValue,
          attributes,
        ) as FF[F];
      case 'number':
        return this.#client.getNumberValue(
          feature,
          defaultValue,
          attributes,
        ) as FF[F];
      case 'object':
        return this.#client.getObjectValue(
          feature,
          defaultValue as JsonValue,
          attributes,
        ) as FF[F];
      default:
        return this.#client.getStringValue(
          feature,
          String(defaultValue),
          attributes,
        ) as FF[F];
    }
  }

  getAllFlags(options: HookOptions = {}): FF {
    return this.#withOverrideControl(options.disableLocalOverrides, () => {
      const allFlags: Record<string, FlagValue> = {};
      for (const [feature, defaultValue] of Object.entries(this.#defaults)) {
        allFlags[feature] = this.#getValue(
          feature,
          defaultValue as FF[string],
          options.attributes,
        );
      }
      return allFlags as FF;
    });
  }

  #withOverrideControl<T>(disable = false, fn: () => T): T {
    if (this.#overrideProvider && disable) {
      this.#overrideProvider.setSkipOverrides(true);
    }
    try {
      return fn();
    } finally {
      if (this.#overrideProvider) {
        this.#overrideProvider.setSkipOverrides(false);
      }
    }
  }

  get overrides() {
    return this.getAllOverrides()
      .filter((override) => override.enabled)
      .reduce(
        (flags, override) => {
          flags[override.key] = override.value;
          return flags;
        },
        {} as Record<string, FlagValue>,
      );
  }

  getAllOverrides() {
    return this.#overrideProvider?.getAllOverrides() ?? [];
  }

  setOverride(override: FlagOverride) {
    return this.#overrideProvider?.setOverride(override);
  }

  setOverrides(
    overridesOrUpdater: Partial<FF> | ((prev: Partial<FF>) => Partial<FF>),
  ) {
    const overrides =
      typeof overridesOrUpdater === 'function'
        ? overridesOrUpdater(this.overrides as FF)
        : overridesOrUpdater;

    Object.entries(overrides)
      .filter((entry): entry is [string, FlagValue] => entry[1] !== undefined)
      .forEach(([key, value]) => {
        this.#overrideProvider?.setOverride({
          key,
          value,
          type: this.#getType(value),
          enabled: true,
        });
      });

    this.getAllOverrides().forEach((override) => {
      if (!(override.key in overrides)) {
        this.removeOverride(override.key);
      }
    });
  }

  #getType(value: FlagValue): FlagType {
    let type = typeof value;
    switch (type) {
      case 'boolean':
      case 'number':
      case 'object':
        return type;
      default:
        return 'string';
    }
  }

  removeOverride(feature: keyof FF & string) {
    return this.#overrideProvider?.removeOverride(feature);
  }

  clearOverrides() {
    return this.#overrideProvider?.clearOverrides();
  }
}
